import React, { useState, useEffect, FC } from 'react'
import { useTranslation } from 'next-i18next'
import GrainButton from '@/shared/GrainButton'
import { ArrowSVGIcon, CheckMarkSVGIcon } from '@/components/icons'

type AccountLockedProps = {
  requestState: {
    isFetching: boolean,
  },
  email: string,
  onBackToLoginClick: () => void,
  onUnlockAccountPressed: (event: React.MouseEvent<HTMLButtonElement>) => void,
}

export const AccountLocked :FC<AccountLockedProps> = ({
  requestState,
  email,
  onBackToLoginClick,
  onUnlockAccountPressed,
}) => {
  const { t } = useTranslation(['login', 'common'], { nsMode: 'fallback' })
  const [isWaiting, setIsWaiting] = useState(false)
  const [timeLeft, setTimeLeft] = useState(10)

  const onClick = event => {
    setIsWaiting(true)
    onUnlockAccountPressed(event)
  }

  // eslint-disable-next-line consistent-return
  useEffect(() => {
    if (isWaiting) {
      const timer = setTimeout(() => {
        setTimeLeft(timeLeft - 1)
      }, 1000)

      if (timeLeft <= 0) {
        clearTimeout(timer)
        setIsWaiting(false)
      }

      return () => {
        clearTimeout(timer)
      }
    }

    if (!isWaiting && timeLeft === 0) {
      setTimeLeft(10)
    }
  }, [timeLeft, isWaiting])

  return (
    <div className="forgot-password relative rounded-xl border border-gray-1 bg-gray-6-filled [box-shadow:var(--form-shadow)]">
      <div className="step-header flex max-h-[88px] min-h-[88px] items-center justify-start gap-6 border-b border-b-gray-5 p-6">
        <GrainButton
          variant="secondary"
          onClick={onBackToLoginClick}
          text={t('back')}
          icon={<ArrowSVGIcon />}
          iconLeft
        />
        <div className="top-text">
          <h1 className="mb-0">{t('account_locked')}</h1>
          <div className="caption-text text-gray-3">
            {t('too_many_failed_attempts')}
          </div>
        </div>
      </div>

      <div className="step-body flex flex-col gap-6 p-6">
        <p className="reset-text">
          {t('account_locked_text')}
          {' '}
          {email}
        </p>
      </div>

      <div className="step-footer flex gap-2 border-t border-t-gray-5 p-6 [&_button]:flex-[1_1_0px]">
        <GrainButton
          fullWidth
          type="submit"
          isOnSecondary
          variant="primary"
          text={isWaiting ? t('link_sent_resend_in', { seconds: timeLeft }) : t('send_reset_password_link')}
          className="reset-password-button"
          isLoading={requestState.isFetching}
          onClick={onClick}
          isDisabled={isWaiting}
          icon={isWaiting ? <CheckMarkSVGIcon /> : <></>}
        />
      </div>
    </div>
  )
}
